import useSWR from 'swr';
import { usePolyAuth } from './PolyAuthContext';
import { isSvgFile, svgFileToPng } from '../utils/svgToPng';
import { wrapApiError } from '../utils/apiError';

/**
 * Custom hook to fetch and manage apps for a specific account
 * @returns {object} SWR response with data, error, isLoading, mutate, and CRUD methods
 */
export const useAccountApps = () => {
  const { api_client, accountId, local_fetcher } = usePolyAuth();

  const { data, error, mutate, isLoading, isValidating } = useSWR(
    api_client && accountId && accountId != 1000000000 ? `/account/${accountId}/apps/` : null,
    local_fetcher
  );

  // Helper for optimistic updates
  const optimisticUpdate = async (updateFn, apiCall) => {
    const optimisticData = updateFn(data || []);
    await mutate(optimisticData, false);
    
    try {
      await apiCall();
    } catch (error) {
      // If failed, revalidate to get the correct data back
      mutate();
      throw error;
    }
  };

  const createApp = async (appData) => {
    try {
      const response = await api_client.post(`/account/${accountId}/apps/`, appData);
      // Optimistically push the new app into the cache and await revalidation
      // so callers can immediately navigate to /status/<new_app_id> without
      // racing the SWR refetch (otherwise AppStatusPage falls back to apps[0]).
      await mutate(
        (current) => (current ? [...current, response.data] : [response.data]),
        { revalidate: true }
      );
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to create app');
    }
  };

  const updateApp = async (appId, appData) => {
    try {
      const response = await api_client.patch(`/account/${accountId}/apps/${appId}/`, appData);
      mutate();
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to update app');
    }
  };

  const deleteApp = async (appId) => {
    try {
      await api_client.delete(`/account/${accountId}/apps/${appId}/`);
      mutate();
    } catch (error) {
      throw wrapApiError(error, 'Failed to delete app');
    }
  };

  /**
   * Get a presigned URL for uploading an app logo
   * @returns {Promise<{upload_url: string, cdn_url: string}>}
   */
  const getAppLogoUploadUrl = async (appId, contentType, logoType = 'light') => {
    try {
      const response = await api_client.post(
        `/account/${accountId}/apps/${appId}/logo-upload-url`,
        { logo_type: `app-${logoType}`, content_type: contentType }
      );
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get upload URL');
    }
  };

  const uploadAppLogoToS3 = async (appId, file, logoType = 'light') => {
    // SVG is rasterized to PNG client-side so mobile/web only ever render a
    // safe raster; the backend presigned path still only accepts image/png.
    const upload = isSvgFile(file) ? await svgFileToPng(file) : file;

    const { upload_url, cdn_url } = await getAppLogoUploadUrl(appId, upload.type, logoType);
    await fetch(upload_url, {
      method: 'PUT',
      body: upload,
      headers: { 'Content-Type': upload.type },
    });
    return cdn_url;
  };

  /**
   * Theme CSS uploads as text/css whatever the browser reports.
   * @returns {Promise<string>} the CDN URL to store in prescreen_branding
   */
  const uploadBrandAssetToS3 = async (appId, file, assetType) => {
    const isCss = assetType === 'theme-css';
    try {
      // Inside the try so a rasterization failure wraps like a transport one.
      const upload = !isCss && isSvgFile(file) ? await svgFileToPng(file) : file;
      const contentType = isCss ? 'text/css' : upload.type;
      const response = await api_client.post(
        `/account/${accountId}/apps/${appId}/logo-upload-url`,
        { logo_type: assetType, content_type: contentType }
      );
      const { upload_url, cdn_url } = response.data;
      await fetch(upload_url, {
        method: 'PUT',
        body: upload,
        headers: { 'Content-Type': contentType },
      });
      return cdn_url;
    } catch (error) {
      throw wrapApiError(error, 'Failed to upload brand asset');
    }
  };

  const getAppWebhook = async (appId) => {
    try {
      const response = await api_client.get(`/account/${accountId}/apps/${appId}/webhook`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get webhook');
    }
  };

  const createAppWebhook = async (appId, webhookData) => {
    try {
      const response = await api_client.post(`/account/${accountId}/apps/${appId}/webhook`, webhookData);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to create webhook');
    }
  };

  const updateAppWebhook = async (appId, webhookData) => {
    try {
      const response = await api_client.patch(`/account/${accountId}/apps/${appId}/webhook`, webhookData);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to update webhook');
    }
  };

  const deleteAppWebhook = async (appId) => {
    try {
      await api_client.delete(`/account/${accountId}/apps/${appId}/webhook`);
    } catch (error) {
      throw wrapApiError(error, 'Failed to delete webhook');
    }
  };

  const getServiceNowCredentials = async (appId) => {
    try {
      const response = await api_client.get(`/account/${accountId}/apps/${appId}/servicenow-credentials`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get ServiceNow credentials');
    }
  };

  const updateServiceNowCredentials = async (appId, data) => {
    try {
      const response = await api_client.put(`/account/${accountId}/apps/${appId}/servicenow-credentials`, data);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to update ServiceNow credentials');
    }
  };

  const getTrustCheckEmailTemplate = async (appId) => {
    try {
      const response = await api_client.get(
        `/account/${accountId}/apps/${appId}/trust-check-email-template`
      );
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to load email template');
    }
  };

  const saveTrustCheckEmailTemplate = async (appId, { subject, bodyHtml }) => {
    try {
      const response = await api_client.put(
        `/account/${accountId}/apps/${appId}/trust-check-email-template`,
        { subject, body_html: bodyHtml }
      );
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to save email template');
    }
  };

  const resetTrustCheckEmailTemplate = async (appId) => {
    try {
      const response = await api_client.delete(
        `/account/${accountId}/apps/${appId}/trust-check-email-template`
      );
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to reset email template');
    }
  };

  const listAppApiKeys = async (appId) => {
    try {
      const response = await api_client.get(`/account/${accountId}/apps/${appId}/api-keys`);
      return response.data.api_keys || [];
    } catch (error) {
      throw wrapApiError(error, 'Failed to list API keys');
    }
  };

  const createAppApiKey = async (appId, payload) => {
    try {
      const response = await api_client.post(`/account/${accountId}/apps/${appId}/api-keys`, payload);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to create API key');
    }
  };

  const revokeAppApiKey = async (appId, apiKeyId) => {
    try {
      await api_client.delete(`/account/${accountId}/apps/${appId}/api-keys/${apiKeyId}`);
    } catch (error) {
      throw wrapApiError(error, 'Failed to revoke API key');
    }
  };

  return {
    apps: data || [],
    isLoading,
    isValidating,
    isError: error,
    error,
    mutate,
    optimisticUpdate,
    createApp,
    updateApp,
    deleteApp,
    getAppLogoUploadUrl,
    uploadAppLogoToS3,
    uploadBrandAssetToS3,
    getAppWebhook,
    createAppWebhook,
    updateAppWebhook,
    deleteAppWebhook,
    getServiceNowCredentials,
    updateServiceNowCredentials,
    getTrustCheckEmailTemplate,
    saveTrustCheckEmailTemplate,
    resetTrustCheckEmailTemplate,
    listAppApiKeys,
    createAppApiKey,
    revokeAppApiKey,
  };
};